/**
 * S-37 «Ещё» — хаб разделов, и заглушка ещё не готового раздела.
 *
 * ⛔ Внутренние коды (номер экрана «S-13», код ссылки «ads») наружу не выводятся: вместо них —
 * название, подпись и иконка раздела (04-voice, 3.14; 06-icons, 7.7).
 */
import { SCREENS, isVisibleFor, type ScreenId, type ScreenSpec } from '../router/screens.js'
import type { Session } from '../api/client.js'
import { haptic } from '../telegram/call.js'
import { Icon, type IconName } from '../components/icons/Icon.js'
import { Empty } from './common.js'

/** Подпись и иконка пункта хаба. Порядок групп и пунктов — по частоте использования, не по номеру. */
export const ITEM: Partial<Record<ScreenId, { sub?: string; icon: IconName; title?: string }>> = {
  'S-13': { sub: 'Кампании, ставки, минус-фразы', icon: 'agent-ads' },
  'S-16': { sub: 'Запас товара и план поставок', icon: 'agent-stock' },
  'S-18': { sub: 'Очередь ответов покупателям', icon: 'agent-feedback' },
  'S-09': { sub: 'Все отчёты агентов по дням', icon: 'section-reports' },
  'S-22': { sub: 'Что сделано по предложениям агентов', icon: 'section-history' },
  'S-23': { sub: 'Какие отчёты приходят и когда', icon: 'section-agents' },
  'S-25': { sub: 'Сигналы, вечерняя сводка, тихие часы', icon: 'section-schedule' },
  'S-26': { sub: 'Прибыль со штуки по каждой модели, как в WB', icon: 'section-cost' },
  'S-44': { sub: 'Сколько заработали — по дням, моделям и партиям', icon: 'agent-finance' },
  'S-45': { sub: 'Закрывшийся месяц в Excel: сводка, модели, дни, отчёты WB', icon: 'agent-finance' },
  'S-28': { sub: 'Налог, постоянные расходы, пополнение', icon: 'section-expenses' },
  'S-06': { sub: 'Срок действия и доступы', icon: 'section-key' },
  'S-33': { sub: 'Владельцы, сотрудники, приглашения', icon: 'section-access' },
  'S-29': { sub: 'Загрузка данных, ИИ, ошибки', icon: 'section-ops' },
  'S-40': { sub: 'Сколько времени уходит на кабинет', icon: 'section-timer' },
  'S-32': { sub: 'Частые вопросы', icon: 'section-help' },
  'S-39': { sub: 'Что Wildberries не отдаёт и почему', icon: 'section-blind' },
  'S-30': { sub: 'Что агенты получают на вход', icon: 'section-prompts' },
  'S-01': { sub: 'Коротко о Штабе, по шагам', icon: 'action-tip', title: 'Как это работает' },
}

/** Группы — ТЗ S-37 («Работа · Настройка · Система · Справка») словами 04-voice. */
export const GROUPS: Array<{ title: string; ids: ScreenId[] }> = [
  { title: 'Отделы штаба', ids: ['S-44', 'S-45', 'S-13', 'S-16', 'S-18', 'S-09', 'S-22'] },
  { title: 'Настройка', ids: ['S-23', 'S-25', 'S-26', 'S-28'] },
  { title: 'Магазин и доступ', ids: ['S-06', 'S-33', 'S-29', 'S-40'] },
  { title: 'Справка', ids: ['S-32', 'S-39', 'S-30', 'S-01'] },
]

export function ScreenMore(
  { role, open }: { role: Session['role']; open: (id: ScreenId) => void },
): React.JSX.Element {
  // Пункты, недоступные роли, не показываются вовсе, а не блокируются: заблокированный
  // пункт сообщает о существовании раздела, к которому доступа нет.
  const visible = (id: ScreenId): boolean => isVisibleFor(SCREENS[id], role)
  // Раздел вкладки «Ещё», не попавший ни в одну группу, не теряется: идёт последней группой.
  const grouped = new Set(GROUPS.flatMap((g) => g.ids))
  const rest = Object.values(SCREENS)
    .filter((s) => s.tab === 'T4' && s.depth === 1 && !grouped.has(s.id) && visible(s.id))
    .map((s) => s.id)
  const groups = [...GROUPS, ...(rest.length > 0 ? [{ title: 'Другое', ids: rest }] : [])]

  return (
    <div data-testid="screen-body">
      {groups.map((g) => {
        const ids = g.ids.filter(visible)
        if (ids.length === 0) return null
        return (
          <section key={g.title}>
            <h2 className="sect wb-overline"><span>{g.title}</span></h2>
            <div className="card">
              {ids.map((id) => {
                const s = SCREENS[id]
                const it = ITEM[id]
                return (
                  <button type="button" className="row row-tap has-ic" key={id} data-testid={'more-' + id}
                    onClick={() => { haptic('light'); open(id) }}>
                    <Icon name={it?.icon ?? 'nav-next'} className="ic-muted" />
                    <span className="row-main">
                      <span className="row-title">{it?.title ?? s.title}</span>
                      {it?.sub !== undefined && <span className="row-meta">{it.sub}</span>}
                    </span>
                    <Icon name="nav-next" className="ic-chev row-end" />
                  </button>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}

/**
 * Экран, которого нет в этой сборке (раздел следующего релиза — «Экспорт», v1.1, в MVP маршрута нет).
 * Сюда не ведёт ни одна строка и ни одна ссылка; если всё же попали — говорим, где нужное сейчас,
 * без обещаний «скоро».
 */
export function Placeholder(
  { spec, onReports }: { spec: ScreenSpec; onReports: () => void },
): React.JSX.Element {
  return (
    <div data-testid="screen-body">
      <Empty icon={ITEM[spec.id]?.icon ?? 'section-help'} testid="placeholder"
        title={`Раздела «${spec.title}» в этой версии нет`}
        text="Всё главное по магазину — в утренних отчётах и разделах «Ещё».">
        <button type="button" className="btn btn-soft" data-testid="placeholder-reports" onClick={onReports}>Открыть отчёты</button>
      </Empty>
    </div>
  )
}
